import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, Phone, MapPin, ArrowLeft, Send, Loader2, CheckCircle2,
  Clock, Zap, Globe2,
} from "lucide-react";

const INQUIRY_TYPES = [
  "Procurement Inquiry",
  "Trade Partnership",
  "Supplier Registration",
  "General Inquiry",
];

export default function Contact() {
  const location = useLocation();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    phone: "",
    inquiry_type: "Procurement Inquiry",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-fill message if redirected from a product
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const p = params.get("product");
    if (p) {
      setForm((f) => ({
        ...f,
        inquiry_type: "Procurement Inquiry",
        message: `I'd like to receive specifications and pricing for ${p.replace(/-/g, " ")}.`,
      }));
    }
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — our trade desk will reply within 1 business day.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/traderepubliq-logo.svg" alt="TradeRepubliq" className="h-7 md:h-8" />
          </Link>
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-stone-600 hover:text-[#6B0F1A]" data-testid="contact-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="mesh-blob mesh-blob-1" />
          <div className="mesh-blob mesh-blob-2" />
          <div className="absolute inset-0 dotgrid" />
          <div className="absolute inset-0 grain" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white" />
        </div>
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-[#6B0F1A]/15 text-[#6B0F1A] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5 shadow-sm">
            <Globe2 className="w-3.5 h-3.5" /> Contact Us
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight text-[#1F1F1F] leading-[1.05] max-w-3xl mx-auto">
            Connect With <span className="grad-text">Our Trade Desk</span>
          </h1>
          <p className="text-stone-600 text-lg mt-5 max-w-2xl mx-auto">
            Whether you're looking to source commodities or explore partnership opportunities, our team is ready to assist.
          </p>
        </div>
      </section>

      {/* Form + Info grid */}
      <section className="max-w-6xl mx-auto px-6 pb-20 grid lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-3 order-2 lg:order-1">
          <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 shadow-sm">
            <div className="mb-6">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#1F1F1F]">Send an Inquiry</h2>
              <p className="text-stone-500 text-sm mt-1">Fill out the form below and we'll get back to you promptly.</p>
            </div>

            {sent ? (
              <SuccessState onAnother={() => { setSent(false); setForm({ full_name: "", email: "", company: "", phone: "", inquiry_type: "Procurement Inquiry", message: "" }); }} />
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Full Name" required>
                    <input
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className="input"
                      data-testid="contact-name"
                      placeholder="John Doe"
                    />
                  </Field>
                  <Field label="Email Address" required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input"
                      data-testid="contact-email"
                      placeholder="name@company.com"
                    />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Company">
                    <input
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="input"
                      data-testid="contact-company"
                      placeholder="Your company"
                    />
                  </Field>
                  <Field label="Phone Number">
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="input"
                      data-testid="contact-phone"
                      placeholder="+971 …"
                    />
                  </Field>
                </div>
                <Field label="Inquiry Type" required>
                  <select
                    required
                    value={form.inquiry_type}
                    onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
                    className="input"
                    data-testid="contact-type"
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message" required>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="input"
                    data-testid="contact-message"
                    placeholder="Tell us about your requirement — products, volumes, destinations, timelines…"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#6B0F1A] hover:bg-[#56121C] disabled:opacity-60 text-white font-semibold rounded-xl py-3.5 shadow-xl shadow-[#6B0F1A]/30 shine"
                  data-testid="contact-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-2 order-1 lg:order-2 space-y-4">
          <InfoCard
            title="Dubai Headquarters"
            badge={<MapPin className="w-5 h-5" />}
            tone="brand"
          >
            <div className="text-stone-600 text-sm leading-relaxed">
              Meydan Grandstand, 6th Floor<br />
              Meydan Road, Nad Al Sheba<br />
              Dubai, United Arab Emirates
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B0F1A]">
              <Clock className="w-3.5 h-3.5" /> Mon – Fri · 9:00 AM – 6:00 PM (GST)
            </div>
          </InfoCard>

          <div className="grid grid-cols-1 gap-3">
            <InfoLink
              icon={<Mail className="w-5 h-5" />}
              label="Email"
              value="support@traderepubliq.com"
              href="mailto:support@traderepubliq.com"
              testId="info-email"
            />
            <InfoLink
              icon={<Phone className="w-5 h-5" />}
              label="Phone"
              value="+971 58 112 0336"
              href="tel:+971581120336"
              testId="info-phone"
            />
          </div>

          <InfoCard title="Quick Response" badge={<Zap className="w-5 h-5" />} tone="accent">
            <div className="text-stone-700 text-sm leading-relaxed">
              Our trade desk responds to all inquiries within <span className="font-semibold text-[#6B0F1A]">one business day</span>. For urgent needs, call us directly.
            </div>
          </InfoCard>

          {/* Map link block */}
          <a
            href="https://www.google.com/maps/search/Meydan+Grandstand+Dubai"
            target="_blank"
            rel="noreferrer"
            className="block relative overflow-hidden rounded-2xl border border-stone-200 p-5 bg-gradient-to-br from-[#F4E8E9] to-white hover-lift"
            data-testid="map-link"
          >
            <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(107,15,26,0.2) 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
            <div className="relative flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-[#A8323F]">Map</div>
                <div className="font-display font-bold text-[#1F1F1F] mt-0.5">View on Google Maps</div>
                <div className="text-xs text-stone-500 mt-1">Meydan Grandstand, Dubai, U.A.E.</div>
              </div>
              <MapPin className="w-8 h-8 text-[#6B0F1A]" />
            </div>
          </a>
        </div>
      </section>

      <style>{`
        .input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.75rem; padding:.85rem 1rem; color:#1F1F1F; outline:none; font-size: 0.95rem; transition: all 0.2s; }
        .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 4px rgba(168,50,63,0.12);}
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-600">
        {label}{required && <span className="text-[#A8323F] ml-1">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function InfoCard({ title, badge, tone, children }) {
  const toneCls = tone === "accent"
    ? "border-[#A8323F]/30 bg-gradient-to-br from-[#FAF3F4] to-white"
    : "border-stone-200 bg-white";
  const badgeCls = tone === "accent" ? "bg-[#A8323F] text-white" : "bg-[#6B0F1A] text-white";
  return (
    <div className={`rounded-2xl border p-5 ${toneCls}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${badgeCls}`}>{badge}</div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold tracking-tight text-[#1F1F1F]">{title}</div>
          <div className="mt-1.5">{children}</div>
        </div>
      </div>
    </div>
  );
}

function InfoLink({ icon, label, value, href, testId }) {
  return (
    <a href={href} className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4 hover:border-[#A8323F] hover-lift group" data-testid={testId}>
      <div className="w-10 h-10 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center group-hover:bg-[#6B0F1A] group-hover:text-white transition-colors">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{label}</div>
        <div className="font-semibold text-[#1F1F1F] truncate">{value}</div>
      </div>
    </a>
  );
}

function SuccessState({ onAnother }) {
  return (
    <div className="text-center py-10" data-testid="contact-success">
      <div className="w-16 h-16 rounded-full bg-[#F4E8E9] text-[#6B0F1A] mx-auto flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <h3 className="font-display text-2xl font-bold text-[#1F1F1F] mt-4">Message sent.</h3>
      <p className="text-stone-500 text-sm mt-2 max-w-sm mx-auto">
        Thank you — our trade desk will be in touch within <span className="font-semibold text-[#6B0F1A]">one business day</span>.
      </p>
      <button onClick={onAnother} className="mt-6 text-sm font-semibold text-[#6B0F1A] hover:text-[#56121C]" data-testid="send-another">
        Send another inquiry →
      </button>
    </div>
  );
}
